<template>
  <div class="flex relative bg-neutral-background-secondary px-6 py-5 gap-4">
    <div class="flex-1">
      <template v-if="review.includedReviewItems.value.length">
        <div
          class="inline-grid grid-cols-2 w-full border-t border-r border-neutral-border-default rounded-lg"
        >
          <div
            class="bg-neutral-background-default h-12.5 rounded-tl-lg sticky top-13 left-0 z-51"
          >
            <div
              class="h-full text-sm px-5 py-3.5 justify-between border-neutral-border-default border-b border-l flex items-center font-medium"
            >
              <span class="text-neutral-text-title">Records</span>
              <div class="flex items-center gap-2">
                <Sort />
                <ReviewItemFilters />
              </div>
            </div>
          </div>

          <div
            class="bg-neutral-background-default h-12.5 sticky top-13 left-0 z-40 rounded-tr-lg"
          >
            <div
              :title="sortedAttributes[selectedAttributeIndex]?.question"
              class="h-full text-sm border-l border-b border-neutral-border-default text-neutral-text-title flex p-3.5 items-center font-medium justify-between"
            >
              <div class="truncate">
                {{
                  sortedAttributes[selectedAttributeIndex]?.label !== ''
                    ? sortedAttributes[selectedAttributeIndex]?.label
                    : 'Question unspecified'
                }}
              </div>
            </div>
          </div>

          <template v-if="paginatedIncludedReviewItems.length">
            <ReviewItemDataExtraction
              v-for="reviewItem in paginatedIncludedReviewItems"
              :key="
                reviewItem.id + sortedAttributes[selectedAttributeIndex]?.id
              "
              :attribute="sortedAttributes[selectedAttributeIndex]"
              :is-metadata-expanded="isMetadataExpanded"
              :review-item="reviewItem"
            />
          </template>

          <div v-else class="col-span-2 p-4 text-center text-gray-500">
            No articles match your filters
          </div>
        </div>
        <div
          v-if="review.filteredIncludedReviewItems.value.length > 0"
          class="w-full flex items-center justify-between px-5 py-4 sticky bottom-0 bg-neutral-background-default border-r border-l border-b border-neutral-border-default rounded-b-lg z-50"
        >
          <p class="text-base font-medium text-neutral-text-title">
            Total Records: {{ review.filteredIncludedReviewItems.value.length }}
          </p>
          <Pagination
            v-model:items-per-page="itemsPerPage"
            v-model:page="currentPage"
            :total-items="review.filteredIncludedReviewItems.value.length"
          />
        </div>
      </template>

      <div v-else-if="review.entity.value?.studies?.length" class="h-full">
        No articles have been included
      </div>
      <div v-else>This review is empty</div>
    </div>

    <div
      v-if="paginatedIncludedReviewItems.length"
      class="w-[260px] sticky top-13 right-0 h-[calc(100vh-80px)] overflow-y-auto flex flex-col items-start gap-3 z-1"
    >
      <button
        v-for="(attribute, index) in sortedAttributes"
        :key="attribute.id"
        class="px-3.5 py-2 w-full text-left rounded-lg transition-colors flex justify-between hover:bg-white hover:shadow hover:shadow-black/10"
        :class="{
          'bg-white shadow shadow-black/10 text-black':
            index === selectedAttributeIndex,
          'text-gray-400': index !== selectedAttributeIndex,
        }"
        @click="selectedAttributeIndex = index"
      >
        <span
          v-if="attribute.label !== ''"
          :title="attribute.label"
          class="text-base font-medium truncate"
        >
          {{ attribute.label }}
        </span>
        <router-link
          v-else
          v-tooltip.top="'Click to add the missing label in the plan'"
          :to="`/reviews/${review.entity.value.id}/plan/objective`"
          class="text-base font-medium truncate"
          @click.stop
        >
          Question unspecified
        </router-link>
        <CheckCircleIcon
          v-if="attributesFinalizationState[index]"
          class="size-6 text-green-500 shrink-0"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onActivated, onDeactivated, ref, watch } from 'vue'
import { injectStrict } from '@app/utils/injectStrict'
import Sort from '@app/views/Review/Execute/Sort/Sort.vue'

import { useStorage } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import { ReviewKey } from '../../use-review'
import ReviewItemFilters from '../SearchAndFilters/ReviewItemFilters.vue'
import ReviewItemDataExtraction from './ReviewItemDataExtraction/ReviewItemDataExtraction.vue'
import { CheckCircleIcon } from '@heroicons/vue/16/solid'
import Pagination from '@app/components/Global/Pagination/Pagination.vue'

const review = injectStrict(ReviewKey)
const route = useRoute()
const router = useRouter()

const currentPage = computed({
  get() {
    return Number(route.query.page) || 1
  },
  set(value: number) {
    router.replace({
      query: {
        ...route.query,
        page: value.toString(),
      },
    })
  },
})

watch(
  currentPage,
  (newPage) => {
    router.replace({
      query: {
        ...route.query,
        page: newPage.toString(),
      },
    })
  },
  {
    immediate: true,
  },
)

const itemsPerPage = computed({
  get() {
    return Number(route.query.itemsPerPage) || 5
  },
  set(value: number) {
    router.replace({
      query: {
        ...route.query,
        itemsPerPage: value.toString(),
      },
    })
  },
})

const paginatedIncludedReviewItems = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  return review.filteredIncludedReviewItems.value.slice(
    start,
    start + itemsPerPage.value,
  )
})

const sortedAttributes = computed(() =>
  review.entity.value.plan!.synthesisPlan!.attributesStructure.toSorted(
    (a, b) => {
      if (a.order === null && b.order === null) return 0
      if (a.order === null) return -1
      if (b.order === null) return 1
      return a.order > b.order ? 1 : -1
    },
  ),
)

const attributesFinalizationState = computed(() => {
  return sortedAttributes.value.map((attribute) => {
    const sortedAttributeId = attribute.id

    return review.includedReviewItems.value.every((item) => {
      const synthesisAttr = item.synthesis.attributes.find(
        (a) => a.attributeStructureId === sortedAttributeId,
      )

      return synthesisAttr?.isFinalized === true
    })
  })
})

const selectedAttributeIndex = ref(0)

const isMetadataExpanded = useStorage(
  'isMetadataExpanded-' + review.entity.value.id,
  true,
)

const isInExecuteStep = ref(true)
onDeactivated(() => {
  isInExecuteStep.value = false
})

onActivated(() => {
  isInExecuteStep.value = true
})

watch(
  () => review.filtering.terms.value,
  () => {
    currentPage.value = 1
  },
)

watch(
  () => review.filtering.filters.value,
  () => {
    currentPage.value = 1
  },
  { deep: true },
)

watch(itemsPerPage, () => {
  currentPage.value = 1
})
</script>
